import { APP_TIME_ZONE, localDate, localTime } from '@shared/domain/time.ts';

/** "hace X" para el indicador de última sincronización. */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'nunca';
  const seconds = Math.max(0, Math.round((now.getTime() - Date.parse(iso)) / 1000));
  if (seconds < 45) return 'hace unos segundos';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'hace 1 día' : `hace ${days} días`;
}

/** 'dd/mm/aaaa' de una fecha de calendario 'YYYY-MM-DD'. */
export function formatCalendarDate(date: string): string {
  const [y, m, d] = date.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

const LONG_DATE = new Intl.DateTimeFormat('es-CL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});

/** 'lunes 2 de noviembre' de una fecha de calendario 'YYYY-MM-DD'. */
export function formatLongDate(date: string): string {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  return LONG_DATE.format(new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1))).replace(',', '');
}

/** Fecha (y hora si la tiene) de un valor de Notion o un instante. */
export function formatNotionDate(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatCalendarDate(value);
  return `${formatCalendarDate(localDate(value))} ${localTime(value)}`;
}

export function formatFechas(
  fechas: { start: string; end: string | null } | null | undefined,
): string {
  if (!fechas) return '';
  const start = formatNotionDate(fechas.start);
  return fechas.end ? `${start} → ${formatNotionDate(fechas.end)}` : start;
}

/** Fecha y hora local de un instante: 'dd/mm/aaaa HH:MM'. */
export function formatInstant(iso: string): string {
  return `${formatCalendarDate(localDate(iso))} ${localTime(iso)}`;
}

/** Duración 'H:MM:SS' (cronómetro en curso). */
export function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')
  ).toUpperCase();
}

/** Lunes de la semana actual y hoy (fechas locales), para el rango por defecto de reportes. */
export function currentWeekRange(now: Date = new Date()): { from: string; to: string } {
  const today = localDate(now, APP_TIME_ZONE);
  const [y, m, d] = today.split('-').map(Number);
  const weekday = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1)).getUTCDay();
  const monday = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, (d ?? 1) - ((weekday + 6) % 7)));
  return { from: monday.toISOString().slice(0, 10), to: today };
}
